/**
 * Seed the store from a composer `task_created` receipt — the entry-race fix
 * (founder 08-23: "the model is not visible, and also the user side prompt").
 *
 * A composer-born task is minted server-side, so this client has NO local row
 * for it: entering rendered an empty shell until MESSAGE_SYNC's round trip
 * landed. The receipt is emitted only AFTER dispatch persisted the task row
 * and the user-message row, so it is confirmed truth, not optimism — seed the
 * task (title, model) and the ask itself (real row id, persisted identity) so
 * the first frame paints complete. The sync still runs and merges by the same
 * row id; server truth always wins. Never touches a task the store already
 * knows.
 *
 * Own module (not taskStore) purely for the file-size ratchet; it has no
 * state of its own — the task store stays the single source of truth.
 */
import { useTaskStore, buildNewTask } from "./taskStore";
import { DEFAULT_AGENT, type Message } from "./types";

export function seedComposerTask(receipt: {
  taskId: string;
  title: string;
  model?: string;
  prompt?: string;
  messageId?: string;
}): void {
  const store = useTaskStore.getState();
  if (store.tasks.has(receipt.taskId)) return;
  const messages: Message[] =
    receipt.prompt && receipt.messageId
      ? [
          {
            id: receipt.messageId,
            role: "user",
            content: [{ type: "text", text: receipt.prompt }],
            timestamp: new Date(),
            agentSlug: DEFAULT_AGENT,
            identity: { kind: "persisted", serverId: receipt.messageId },
          },
        ]
      : [];
  store._mutateTasks((tasks) => {
    tasks.set(
      receipt.taskId,
      buildNewTask(receipt.taskId, DEFAULT_AGENT, {
        title: receipt.title,
        ...(receipt.model ? { model: receipt.model } : {}),
        messages,
        // The dispatch already happened server-side — the turn is live.
        phase: "processing",
      })
    );
  });
}
